import { ArrowBack, Assignment, CheckCircleOutline, Group, HighlightOff, RemoveCircleOutline, Save, Visibility } from "@mui/icons-material";
import { Box, Button, Checkbox, Chip, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Paper, Radio, RadioGroup, Select, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Tooltip, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { useAtom } from "jotai";
import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import { AuditService } from "../../services/AuditService";
import { AdmissionStatusDTO } from "../../types/AdmissionStatusDTO";
import { AuditAdmissionDTO } from "../../types/AuditAdmissionDTO";
import { AuditLogDTO } from "../../types/AuditLogDTO";
import { useUtils } from "../../utils/utilFunctions";
import { getLoading } from "../execution-events/ExecutionEvents";
import "./AuditAdmission.scss";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

export const stepStatuses = {
	"CONFERENCIA_RH": {status: "Conferência RH", description: "O processo está em conferência pelos Rescursos Humanos.", icon: <Group color="warning"/>},
	"APROVADO": {status: "Aprovado", description: "O processo foi aprovado", icon: <CheckCircleOutline color="success"/> },
	"REPROVADO": {status: "Reprovado", description: "O processo foi reprovado", icon: <HighlightOff color="error"/> },
	"CANCELADO": {status: "Cancelado", description: "O processo foi cancelado", icon: <RemoveCircleOutline/> }
};

export type StepStatusKey = keyof typeof stepStatuses;

function TabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;
	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box sx={{ p: 3 }}>
					<Typography component="div">{children}</Typography>
				</Box>
			)}
		</div>
	);
}

function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		'aria-controls': `simple-tabpanel-${index}`,
	};
}

const AuditAdmission: FC = () => {
	const [permissions] = useAtom(permissionsAtom);
	const { groupid, id } = useParams<{ groupid: string, id: string }>();
	const [admission, setAdmission] = useState<AuditAdmissionDTO | null>(null);
	const [form, setForm] = useState<AdmissionStatusDTO>({ notifyCandidate: false } as AdmissionStatusDTO);
	const [loading, setLoading] = useState(true);
	const [tabValue, setTabValue] = useState(0);
	const [showPersonalInfo, setShowPersonalInfo] = useState(false);
	const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
	const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null);
	const [selectedUser, setSelectedUser] = useState('');
	const [filteredLogs, setFilteredLogs] = useState<AuditLogDTO[]>([]);
	const [showAditionalInfo, setShowAditionalInfo] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const navigate = useNavigate();
	const service = new AuditService();
	const { formatStringFullDate, verifyPermission } = useUtils();
	const { showError, showAlert } = useAlert();

	const personalInfoData = admission ? [
		{ label: 'Nome completo:', value: admission.name },
		{ label: 'CPF:', value: admission.cpf },
		{ label: 'E-mail:', value: admission.email },
		{ label: 'Telefone:', value: admission.phone },
		{ label: 'Data de nascimento:', value: admission.birthDate },
		{ label: 'Sexo:', value: admission.sex === "M" ? "Masculino" : "Feminino" },
	] : [];

	const additionalInfoData = admission ? [
		{ label: 'Raça/Cor:', value: admission.race },
		{ label: 'Escolaridade:', value: admission.education },
		{ label: 'Estado civil:', value: admission.maritalStatus },
		{ label: 'Tamanho do jaleco:', value: admission.clotheSize },
		{ label: 'Número do calçado:', value: admission.shoeSize },
		{ label: 'Nome do contato de emergência:', value: admission.emergencyName },
		{ label: 'Telefone do contato de emergência:', value: admission.emergencyPhone },
		{ label: 'Grau de parentesco do contato de emergência:', value: admission.emergencyType },
		{ label: 'Possui dependentes menores de 14 anos:', value: admission.hasChild ? "Sim" : "Não" },
	] : [];

	const userOptions = useMemo(() => {
		if (!admission?.logs) return [];
		const users = admission.logs.map(log => log.username);
		return [...new Set(users)];
	}, [admission?.logs]);

	const handleChange = (field: keyof AdmissionStatusDTO, value: any) => {
		setForm(prev => ({ ...prev, [field]: value }));
	};

	const loadAdmission = async () => {
		setLoading(true);
		try {
			const response = await service.getAdmission(id || "");
			if (response && response.data) {
				setAdmission(response.data);
			}
		} catch (error) {
			setAdmission(null);
		} finally {
			setLoading(false);
		}
	};

	const handleSaveStatus = async () => {
		if (!id) {
			return;
		}
		setIsSaving(true);
		try {
			if (navigator.geolocation) {
				const { latitude, longitude, locationName } = await getLocation();
				form.latitude = latitude;
				form.longitude = longitude;
				form.locationName = locationName;
			}
			await service.updateAdmissionStatus(id, form);
			setForm({ notifyCandidate: false } as AdmissionStatusDTO);
			await loadAdmission();
			showAlert("Status da admissão atualizado com sucesso.");
		} catch (error) {
			showError("Ocorreu um erro ao salvar o status da admissão.");
		} finally {
			setIsSaving(false);
		}
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setTabValue(newValue);
		setShowPersonalInfo(false);
		setShowAditionalInfo(false);
	};

	const openDocument = async (id: string) => {
		const token = localStorage.getItem("access-token");
		if (!token) {
			return;
		}
		try {
			const { latitude, longitude, locationName } = await getLocation();
			if (navigator.geolocation) {
				form.latitude = latitude;
				form.longitude = longitude;
				form.locationName = locationName;
			}
			const response = await service.getDocument(id, form);
			const documentUrl = URL.createObjectURL(response.data);
			window.open(documentUrl, "_blank");
		} catch (error) {
			console.error("Erro ao abrir documento:", error);
		}
	}

	useEffect(() => {
		if (!id) return;
		loadAdmission();
	}, [id]);

	useEffect(() => {
		if (!id || tabValue !== 3) return;
		loadAdmission();
	}, [tabValue]);

	useEffect(() => {
		if (!admission?.logs) {
			setFilteredLogs([]);
			return;
		};
		let logs = [...admission.logs];
		if (startDate) {
			logs = logs.filter(log => dayjs(log.createdAt).isSameOrAfter(startDate, 'day'));
		}
		if (endDate) {
			logs = logs.filter(log => dayjs(log.createdAt).isSameOrBefore(endDate, 'day'));
		}
		if (selectedUser) {
			logs = logs.filter(log => log.username === selectedUser);
		}
		setFilteredLogs(logs);
	}, [startDate, endDate, selectedUser, admission?.logs]);

	useEffect(() => {
		if (!!permissions.length) {
			verifyPermission(permissions, 'AUDIT');
		}
	}, []);

	return (
		<>
			<AppBar modulo="Admissão" />
			<div className="audit-admission-details">
				<div className="top">
					<Button
						variant="contained"
						color="info"
						startIcon={<ArrowBack />}
						onClick={() => navigate(`/auditoria/${groupid}/admissoes`)}
					>
						Voltar para admissões
					</Button>
				</div>
				{loading ? (
					getLoading()
				) : !admission ? (
					<div className="not-found">Admissão não encontrada.</div>
				) : (
					<>
						{permissions.includes('STATUS_ADMISSION')
							? <Paper className="admission-status-card" elevation={2}>
								<Box className="card-header">
									<Assignment color="info" />
									<Typography variant="h6">Status da admissão</Typography>
								</Box>
								<Grid container spacing={4}>
									<Grid item xs={12} md={4}>
										<Grid container spacing={2}>
											<Grid item xs={12} sm={6}>
												<Typography variant="subtitle2" gutterBottom>Status atual:</Typography>
												<Chip
													icon={stepStatuses[admission.stepStatus as StepStatusKey].icon}
													label={stepStatuses[admission.stepStatus as StepStatusKey].status}
													variant="outlined"
													sx={{ mb: 2, borderRadius: 4 }}
												/>
												<Typography variant="body2" color="text.secondary">
													{stepStatuses[admission.stepStatus as StepStatusKey].description}
												</Typography>
											</Grid>
											<Grid item xs={12} sm={6}>
												<Typography variant="subtitle2" gutterBottom>Alterar para:</Typography>
												<RadioGroup
													value={form.newStatus}
													onChange={(e) => handleChange('newStatus', e.target.value)}
												>
													{(Object.keys(stepStatuses) as StepStatusKey[]).map((status) => (
														<FormControlLabel
															key={status}
															value={status}
															control={<Radio />}
															label={
																<Box className="radio-label">
																	{stepStatuses[status].icon}{stepStatuses[status].status}
																</Box>
															}
														/>
													))}
												</RadioGroup>
											</Grid>
										</Grid>
									</Grid>
									<Grid item xs={12} md={5}>
										<Box className="text-fields-container">
											<TextField
												label="Motivo da alteração: *"
												placeholder="Descreva o motivo da alteração do status..."
												multiline
												rows={2}
												fullWidth
												value={form.reason}
												onChange={(e) => handleChange('reason', e.target.value)}
												helperText="Este motivo será registrado nos logs do sistema."
											/>
											<TextField
												label="Mensagem para o candidato (enviada por e-mail): *"
												placeholder="Escreva a mensagem que será enviada ao candidato..."
												multiline
												rows={2}
												fullWidth
												value={form.message}
												onChange={(e) => handleChange('message', e.target.value)}
												helperText="Personalize a mensagem conforme o status selecionado"
											/>
										</Box>
									</Grid>
									<Grid item xs={12} md={3} className="action-column">
										<Box>
											<FormControlLabel
												className="checkbox-custom-label"
												control={
													<Checkbox
														checked={form.notifyCandidate}
														onChange={(e) => handleChange('notifyCandidate', e.target.checked)}
													/>
												}
												label={
													<Box>
														<Typography variant="body2" fontWeight="bold">Notificar candidato por e-mail</Typography>
														<Typography variant="caption" color="text.secondary">
															O candidato receberá um e-mail com o novo status e a mensagem informada.
														</Typography>
													</Box>
												}
											/>
										</Box>
										<Button
											variant="contained"
											color="success"
											startIcon={<Save />}
											size="large"
											sx={{ mt: 3 }}
											onClick={handleSaveStatus}
											disabled={isSaving || !form.newStatus || !form.reason}
										>
											{isSaving ? "SALVANDO..." : "SALVAR ALTERAÇÃO"}
										</Button>
									</Grid>
								</Grid>
							</Paper>
							: <></>
						}
						<div className="content">
							<Tabs value={tabValue} onChange={handleTabChange} aria-label="abas de detalhes da admissão">
								<Tab label="Dados pessoais" {...a11yProps(0)} />
								<Tab label="Documentos" {...a11yProps(1)} />
								<Tab label="Informações adicionais" {...a11yProps(2)} />
								<Tab label="Logs" {...a11yProps(3)} />
							</Tabs>
							<TabPanel value={tabValue} index={0}>
								<div className="personal-data-tab">
									{showPersonalInfo
										? <></>
										: <div className="panel-controls">
											<Button
												onClick={() => {
													setShowPersonalInfo(!showPersonalInfo);
													createLog("Visualizou dados pessoais");
												}}
												sx={{gap: "1vh"}}
												variant="outlined"
												color="primary"
											>
												Visualizar <Visibility />
											</Button>
										</div>
									}
									<TableContainer sx={{ maxHeight: 700, boxShadow: "0px 0px 1px 1px #b6b6b6" }} component={Paper}>
										<Table stickyHeader sx={{ minWidth: { xs: 'initial', md: 650 } }} aria-label="simple table">
											<TableBody>
												{personalInfoData.map((row) => (
													<TableRow
														key={row.label}
														sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
														className='row-line'
													>
														<TableCell className="bold" scope="row">{row.label}</TableCell>
														<TableCell scope="row" >{showPersonalInfo ? row.value : '••••••••••••••••••'}</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								</div>
							</TabPanel>
							<TabPanel value={tabValue} index={1}>
								{admission.documents && admission.documents.length > 0
									? <TableContainer sx={{ maxHeight: "60vh", boxShadow: "0px 0px 1px 1px #b6b6b6" }} component={Paper}>
										<Table sx={{ minWidth: { xs: 'initial', md: 650 } }} aria-label="tabela de documentos">
											<TableHead>
												<TableRow>
													<TableCell className="bold">Documento</TableCell>
													{permissions.includes("VIEW_AUDIT_DOCUMENTS")
														? <TableCell className="bold">Ações</TableCell>
														: <></>
													}
												</TableRow>
											</TableHead>
											<TableBody>
												{admission.documents.map((document) => (
													<TableRow
														key={document.id}
														sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
													>
														<TableCell scope="row">{document.name}</TableCell>
														{permissions.includes("VIEW_AUDIT_DOCUMENTS")
															? <TableCell>
																<Button variant="contained" onClick={() => openDocument(document.document)}>
																	Visualizar
																</Button>
															</TableCell>
															: <></>
														}
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
									: <div className="sem-execucoes">Nenhum documento encontrado...</div>}
							</TabPanel>
							<TabPanel value={tabValue} index={2}>
								<div className="personal-data-tab">
									{showAditionalInfo
										? <></>
										: <div className="panel-controls">
											<Button
												onClick={() => {
													setShowAditionalInfo(!showAditionalInfo);
													createLog("Visualizou dados adicionais");
												}}
												sx={{gap: "1vh"}}
												variant="outlined"
												color="primary"
											>
												Visualizar <Visibility />
											</Button>
										</div>
									}
									<TableContainer sx={{ maxHeight: 700, boxShadow: "0px 0px 1px 1px #b6b6b6" }} component={Paper}>
										<Table stickyHeader sx={{ minWidth: { xs: 'initial', md: 650 } }} aria-label="simple table">
											<TableBody>
												{additionalInfoData.map((row) => (
													<TableRow
														key={row.label}
														sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
														className='row-line'
													>
														<TableCell className="bold" scope="row">{row.label}</TableCell>
														<TableCell scope="row" >{showAditionalInfo ? row.value : '••••••••••••••••••'}</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								</div>
							</TabPanel>
							<TabPanel value={tabValue} index={3}>
								<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
									<Grid container spacing={2} justifyContent="center" sx={{ mb: 3, width: {xs: "100%", md: "50%"} }}>
										<Grid item xs={12} sm={4}>
											<DatePicker
												label="Data de início"
												value={startDate}
												onChange={(newValue) => setStartDate(newValue)}
												sx={{ width: '100%' }}
											/>
										</Grid>
										<Grid item xs={12} sm={4}>
											<DatePicker
												label="Data de fim"
												value={endDate}
												onChange={(newValue) => setEndDate(newValue)}
												sx={{ width: '100%' }}
											/>
										</Grid>
										<Grid item xs={12} sm={4}>
											<FormControl fullWidth>
												<InputLabel>Usuário</InputLabel>
												<Select
													value={selectedUser}
													label="Usuário"
													onChange={(e) => setSelectedUser(e.target.value)}
												>
													<MenuItem value="">
														<em>Todos</em>
													</MenuItem>
													{userOptions.map(user => (
														<MenuItem key={user} value={user}>{user}</MenuItem>
													))}
												</Select>
											</FormControl>
										</Grid>
									</Grid>
								</LocalizationProvider>
								{filteredLogs && filteredLogs.length > 0
									? <TableContainer sx={{ maxHeight: "60vh", boxShadow: "0px 0px 1px 1px #b6b6b6" }}>
										<Table sx={{ minWidth: { xs: 'initial', md: 650 } }} aria-label="tabela de logs">
											<TableHead>
												<TableRow>
													<TableCell className="bold">Data/Hora</TableCell>
													<TableCell className="bold">Usuário</TableCell>
													<TableCell className="bold">Ação</TableCell>
													<TableCell className="bold">IP</TableCell>
													<TableCell className="bold">Navegador</TableCell>
													<TableCell className="bold">Plataforma</TableCell>
													<TableCell className="bold">Localização</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{filteredLogs.map((log) => (
													<TableRow
														key={log.id}
														sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
													>
														<TableCell scope="row">{formatStringFullDate(log.createdAt)}</TableCell>
														<TableCell>{log.username}</TableCell>
														<TableCell sx={{textOverflow: "ellipsis"}}>
															<Tooltip title={log.action}>
																<Typography>{log.action?.length > 40 ? log.action.slice(0, 40) + "..." : log.action}</Typography>
															</Tooltip>
														</TableCell>
														<TableCell>{log.ip}</TableCell>
														<TableCell>{log.agentFamily || "Não identificado"}</TableCell>
														<TableCell>{log.osFamily || "Não identificado"}</TableCell>
														<TableCell>
															<Tooltip
																title={log.latitude && log.longitude
																	? <span>latitude: {log.latitude}<br />longitude: {log.longitude}</span>
																	: "Não identificado"
																}
															>
																<Typography>
																	{log.locationName || "Não identificado"}
																</Typography>
															</Tooltip>
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
									: <div className="sem-execucoes">Nenhum log encontrado...</div>
								}
							</TabPanel>
						</div>
					</>
				)}
			</div>
		</>
	);

	async function createLog(action: string) {
		if (!id) return;
		const username = localStorage.getItem('username') || "";
		let logPayload = {} as AuditLogDTO;
		logPayload.username = username;
		logPayload.action = action;
		if (navigator.geolocation) {
			const { latitude, longitude, locationName } = await getLocation();
			logPayload.latitude = latitude;
			logPayload.longitude = longitude;
			logPayload.locationName = locationName;
		}
		await service.createLog(id, logPayload);
	}
};

export async function getLocation() {
	let latitude;
	let longitude;
	let locationName;
	try {
		const position = await new Promise<GeolocationPosition>((resolve, reject) =>
			navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
		);
		const lat = position.coords.latitude;
		const lon = position.coords.longitude;
		latitude = lat;
		longitude = lon;
		const response = await fetch(
			`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`
		);
		if (response.ok) {
			const data = await response.json();
			const address = data.address;
			const city = address.city || address.town || address.village || "Cidade desconhecida";
			const state = address.state || "";
			locationName = `${city}, ${state}`.replace(/,\s*$/, "");
		}
	} catch (error) {
		console.error("Não foi possível obter ou traduzir a geolocalização para o log.", error);
	}
	return { latitude, longitude, locationName };
}

export default AuditAdmission;